import { Component } from "react";
import { AlertTriangle, RefreshCw, Home, Mail } from "lucide-react";
import api from "@/lib/api";

/**
 * ErrorBoundary global. Captura cualquier excepción de render del subtree y
 * muestra un fallback amigable en vez de pantalla en blanco.
 * También reporta el error al backend (POST /api/error-log) para tener
 * trazabilidad de errores reales en producción.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: null, stack: null };
  }

  static getDerivedStateFromError(err) {
    return { err };
  }

  componentDidCatch(err, info) {
    const componentStack = info?.componentStack || "";
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", err, componentStack);
    this.setState({ stack: componentStack });
    // Reportar al backend en background (silencioso, no rompe nada si falla)
    try {
      api.post("/error-log", {
        message: err?.message || String(err),
        stack: err?.stack || "",
        component_stack: componentStack,
        url: window.location.href,
        user_agent: navigator.userAgent,
      }).catch(() => {});
    } catch { /* noop */ }
  }

  handleReset = () => {
    this.setState({ err: null, stack: null });
  };

  handleHome = () => {
    this.setState({ err: null, stack: null });
    window.location.href = "/";
  };

  handleReload = () => {
    window.location.reload();
  };

  buildMailto = () => {
    const subject = encodeURIComponent("[FarmaClass] Error en la aplicación");
    const body = encodeURIComponent(
      `Hola,\n\nMe ha aparecido un error en FarmaClass.\n\n` +
      `URL: ${window.location.href}\n` +
      `Mensaje: ${this.state.err?.message || String(this.state.err)}\n\n` +
      `Stack del componente:\n${this.state.stack || "(no disponible)"}\n\n` +
      `Navegador: ${navigator.userAgent}\n`,
    );
    return `mailto:soporte@archiduqueconsulting.com?subject=${subject}&body=${body}`;
  };

  render() {
    if (!this.state.err) return this.props.children;
    const msg = this.state.err?.message || String(this.state.err);
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-8" data-testid="error-boundary">
        <div className="max-w-xl w-full bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <div className="inline-flex items-center gap-2 mb-3 text-rose-600">
            <AlertTriangle className="h-6 w-6" strokeWidth={1.8} />
            <span className="text-[11px] uppercase tracking-[0.22em] font-bold">Algo no ha ido bien</span>
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-950 mb-2">
            Se ha producido un error inesperado
          </h2>
          <p className="text-slate-600 text-sm leading-relaxed mb-4">
            Esta pantalla concreta no se ha podido cargar. Tus datos están seguros y nada se ha perdido.
            Puedes intentar volver al inicio, recargar o avisarnos para que lo arreglemos cuanto antes.
          </p>

          <details className="mb-5 group">
            <summary className="text-xs font-bold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none">
              Detalle técnico (clic para mostrar)
            </summary>
            <pre className="mt-2 text-[11px] bg-slate-50 border border-slate-200 rounded-md p-3 text-slate-700 overflow-auto max-h-48 whitespace-pre-wrap">
              {msg}
              {this.state.stack ? `\n${this.state.stack}` : ""}
            </pre>
          </details>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={this.handleHome}
              data-testid="errboundary-home-btn"
              className="inline-flex items-center gap-1.5 bg-slate-950 text-white text-sm font-bold px-4 py-2.5 rounded-md hover:bg-slate-800"
            >
              <Home className="h-4 w-4" /> Volver al inicio
            </button>
            <button
              onClick={this.handleReload}
              data-testid="errboundary-reload-btn"
              className="inline-flex items-center gap-1.5 bg-white text-slate-900 text-sm font-bold px-4 py-2.5 rounded-md border border-slate-200 hover:border-slate-400"
            >
              <RefreshCw className="h-4 w-4" /> Recargar
            </button>
            <a
              href={this.buildMailto()}
              data-testid="errboundary-mail-btn"
              className="inline-flex items-center gap-1.5 bg-white text-slate-700 text-sm font-bold px-4 py-2.5 rounded-md border border-slate-200 hover:border-slate-400"
            >
              <Mail className="h-4 w-4" /> Avisar a soporte
            </a>
          </div>
        </div>
      </div>
    );
  }
}
